// ===== CORE TYPES =====
import React, { useState, useRef, useCallback, useMemo, memo } from "react";
import { UseFormReturn, Controller, useFieldArray } from "react-hook-form";
import { Switch } from "@shared/ui/switch";
import {
  HelpCircle,
  Check,
  X,
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  EyeOff,
  Eye,
} from "lucide-react";
import {
  FormField,
  FormConfig,
  FormService,
  defaultFormClasses,
} from "../services/FormServices";
import { FieldVerification } from "./FieldVerification";

// Import verification components (you'll need to adjust the path)

interface DynamicFormProps {
  fields: FormField[];
  form: UseFormReturn<any>;
  onSubmit: (data: any) => void | Promise<void>;
  onReset?: () => void;
  loading?: boolean;
  config?: Partial<FormConfig>;
  className?: string;
  onFieldChange?: (fieldName: string, value: any) => void;
  style?: React.CSSProperties;
  renderCustomFooter?: () => React.ReactNode;
  gridColumns?: number;
  responsiveColumns?: {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  onVerificationStatusChange?: (
    fieldName: string,
    status: FormField["verificationStatus"]
  ) => void;
}

// ===== TOOLTIP COMPONENT =====
interface TooltipProps {
  content: string;
  position?: "top" | "bottom" | "left" | "right";
  children: React.ReactNode;
  className?: string;
}

const Tooltip = memo<TooltipProps>(({ content, position = "top", children, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = useMemo(() => ({
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
  }), []);

  const arrowClasses = useMemo(() => ({
    top: "top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-800",
    bottom: "bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-800",
    left: "left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-800",
    right: "right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-800",
  }), []);

  const handlers = useMemo(() => ({
    onMouseEnter: () => setIsVisible(true),
    onMouseLeave: () => setIsVisible(false),
    onClick: () => setIsVisible(!isVisible),
  }), [isVisible]);

  return (
    <div className={`relative inline-block ${className}`}>
      <div {...handlers}>
        {children}
      </div>
      {isVisible && (
        <div className={`absolute z-50 px-3 py-2 text-sm text-gray-700 bg-white rounded-lg shadow-lg border border-gray-200 whitespace-normal ${positionClasses[position]}`}>
          {content}
          <div className={`absolute w-0 h-0 border-4 ${arrowClasses[position]}`} />
        </div>
      )}
    </div>
  );
});

// ===== OPTIMIZED SELECT COMPONENT =====
interface OptimizedSelectProps {
  name: string;
  options: Array<{ value: string | number; label: string; disabled?: boolean; description?: string }>;
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: boolean;
}

const OptimizedSelect = memo<OptimizedSelectProps>(({
  name,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  className = "",
  onFocus,
  onBlur,
  error = false
}) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  }, [onChange]);

  const selectClassName = useMemo(() => {
    const baseClass = "w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
    const errorClass = error ? "border-red-500" : "border-gray-300";
    const disabledClass = disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white";
    return `${baseClass} ${errorClass} ${disabledClass} ${className}`;
  }, [error, disabled, className]);

  const memoizedOptions = useMemo(() => options.map((option, index) => (
    <option
      key={`${name}-${String(option.value)}-${index}`}
      value={String(option.value)}
      disabled={option.disabled}
      title={option.description}
    >
      {option.label}
    </option>
  )), [options, name]);

  return (
    <select
      name={name}
      value={String(value || "")}
      onChange={handleChange}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      className={`form-input`}
    >
      <option value="">
        {placeholder || `-- Select ${name} --`}
      </option>
      {memoizedOptions}
    </select>
  );
});

// ===== FORM ARRAY COMPONENT =====
interface ArrayItemProps {
  itemIndex: number;
  itemFields: FormField[];
  form: UseFormReturn<any>;
  arrayName: string;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canRemove: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  showRemoveButton: boolean;
  showReorderButtons: boolean;
  onFieldChange?: (fieldName: string, value: any) => void;
}

const ArrayItem = memo<ArrayItemProps>(({
  itemIndex,
  itemFields,
  form,
  arrayName,
  onRemove,
  onMoveUp,
  onMoveDown,
  canRemove,
  canMoveUp,
  canMoveDown,
  showRemoveButton,
  showReorderButtons,
  onFieldChange
}) => {
  const formValues = form.watch();

  // Get visible fields for this specific array item
  const visibleFields = useMemo(() => {
    return itemFields.filter(field => {
      if (!field.showWhen) return true;
      
      // Create the field path for this array item
      const fieldPath = `${arrayName}.${itemIndex}.${field.name}`;
      
      // Call showWhen with current form values and the specific field path
      return field.showWhen(formValues, fieldPath);
    });
  }, [itemFields, formValues, arrayName, itemIndex]);

  // Grid classes based on visible field count
  const gridClasses = useMemo(() => {
    const count = visibleFields.length;
    if (count <= 3) {
      return `grid grid-cols-${count} gap-4`;
    } else if (count <= 6) {
      return `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(count, 3)} gap-4`;
    } else {
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4";
    }
  }, [visibleFields.length]);

  // Render individual field with proper path
  const renderField = useCallback((field: FormField) => {
    const fieldName = `${arrayName}.${itemIndex}.${field.name}`;
    
    // Create a new field object with the correct name for this array item
    const arrayField: FormField = {
      ...field,
      name: fieldName
    };

    // Prevent nested form arrays for now
    if (arrayField.type === "form-array") {
      return (
        <div key={field.name} className="p-2 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">Nested form arrays not supported yet</p>
        </div>
      );
    }

    return (
      <FieldRenderer
        key={field.name}
        field={arrayField}
        form={form}
        onFieldChange={onFieldChange}
      />
    );
  }, [arrayName, itemIndex, form, onFieldChange]);

  // Handle the case where no fields are visible
  if (visibleFields.length === 0) {
    return (
      <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
        <div className="flex items-center justify-between">
          <p className="text-yellow-800">
            No fields visible for this item. Please check field conditions.
          </p>
          {showRemoveButton && canRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              title="Remove item"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
      <div className="flex items-start gap-4">
        {/* Reorder Controls */}
        {showReorderButtons && (
          <div className="flex flex-col gap-1 mt-8">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Move up"
            >
              <GripVertical className="w-4 h-4 rotate-90" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Move down"
            >
              <GripVertical className="w-4 h-4 -rotate-90" />
            </button>
          </div>
        )}

        {/* Fields */}
        <div className={`flex-1 ${gridClasses}`}>
          {visibleFields.map(renderField)}
        </div>

        {/* Remove Button */}
        {showRemoveButton && canRemove && (
          <div className="mt-8">
            <button
              type="button"
              onClick={onRemove}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              title="Remove item"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Item Footer */}
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
        <span className="text-xs text-gray-500">
          Item #{itemIndex + 1} ({visibleFields.length} fields visible)
        </span>
      </div>
    </div>
  );
});

ArrayItem.displayName = "ArrayItem";

// Form Array Component
interface FormArrayProps {
  name: string;
  form: UseFormReturn<any>;
  itemFields: FormField[];
  label?: string;
  helperText?: string;
  minItems?: number;
  maxItems?: number;
  className?: string;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  showReorderButtons?: boolean;
  addButtonText?: string;
  emptyMessage?: string;
  onFieldChange?: (fieldName: string, value: any) => void;
}

const FormArrayComponent = memo<FormArrayProps>(({
  name,
  form,
  itemFields,
  label,
  helperText,
  minItems = 1,
  maxItems,
  className = "",
  showAddButton = true,
  showRemoveButton = true,
  showReorderButtons = false,
  addButtonText = "Add Item",
  emptyMessage = "No items added yet",
  onFieldChange
}) => {
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name,
  });

  // Create default item based on itemFields
  const createDefaultItem = useCallback(() => {
    const defaultItem: any = {};
    itemFields.forEach((field) => {
      switch (field.type) {
        case "number":
        case "decimal":
        case "negative-decimal":
          defaultItem[field.name] = field.min !== undefined ? field.min : 0;
          break;
        case "checkbox":
          defaultItem[field.name] = false;
          break;
        case "multi-select":
        case "form-array":
          defaultItem[field.name] = [];
          break;
        case "select":
          defaultItem[field.name] = "";
          break;
        default:
          defaultItem[field.name] = field.defaultValue !== undefined ? field.defaultValue : "";
      }
    });
    return defaultItem;
  }, [itemFields]);

  // Memoized handlers
  const handlers = useMemo(() => ({
    addItem: () => {
      if (!maxItems || fields.length < maxItems) {
        const newItem = createDefaultItem();
        append(newItem);
      }
    },
    removeItem: (index: number) => {
      if (fields.length > minItems) {
        remove(index);
      }
    },
    moveItem: (from: number, to: number) => {
      if (to >= 0 && to < fields.length) {
        move(from, to);
      }
    }
  }), [fields.length, maxItems, minItems, createDefaultItem, append, remove, move]);

  // Validation flags
  const canAddItem = !maxItems || fields.length < maxItems;
  const canRemoveItem = fields.length > minItems;

  // Memoized item renderers
  const renderArrayItems = useMemo(() => {
    return fields.map((field, index) => (
      <ArrayItem
        key={field.id}
        itemIndex={index}
        itemFields={itemFields}
        form={form}
        arrayName={name}
        onRemove={() => handlers.removeItem(index)}
        onMoveUp={() => handlers.moveItem(index, index - 1)}
        onMoveDown={() => handlers.moveItem(index, index + 1)}
        canRemove={canRemoveItem}
        canMoveUp={index > 0}
        canMoveDown={index < fields.length - 1}
        showRemoveButton={showRemoveButton}
        showReorderButtons={showReorderButtons}
        onFieldChange={onFieldChange}
      />
    ));
  }, [
    fields,
    itemFields,
    form,
    name,
    handlers,
    canRemoveItem,
    showRemoveButton,
    showReorderButtons,
    onFieldChange
  ]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      {label && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{label}</h3>
          {showAddButton && canAddItem && (
            <button
              type="button"
              onClick={handlers.addItem}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {addButtonText}
            </button>
          )}
        </div>
      )}

      {helperText && (
        <p className="text-xs text-gray-600">{helperText}</p>
      )}

      {/* Items Container */}
      {fields.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <p>{emptyMessage}</p>
          {showAddButton && canAddItem && (
            <button
              type="button"
              onClick={handlers.addItem}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            >
              <Plus className="w-4 h-4" />
              {addButtonText}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {renderArrayItems}
        </div>
      )}

      {/* Bottom Add Button */}
      {fields.length > 0 && showAddButton && canAddItem && (
        <button
          type="button"
          onClick={handlers.addItem}
          className="w-full py-3 text-sm font-medium text-blue-600 bg-blue-50 border-2 border-dashed border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <Plus className="w-4 h-4 inline mr-2" />
          {addButtonText}
        </button>
      )}

      {/* Footer Info */}
      {maxItems && (
        <div className="text-xs text-gray-500 text-center">
          {fields.length} of {maxItems} items
        </div>
      )}
    </div>
  );
});
// ===== OPTIMIZED MULTI-SELECT COMPONENT =====
interface MultiSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  description?: string;
}

interface OptimizedMultiSelectProps {
  name: string;
  options: MultiSelectOption[];
  value: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxDisplayItems?: number;
  searchable?: boolean;
  clearable?: boolean;
  closeOnSelect?: boolean;
  maxSelection?: number;
}

const OptimizedMultiSelect = memo<OptimizedMultiSelectProps>(({
  name,
  options,
  value = [],
  onChange,
  placeholder = "Select options...",
  disabled = false,
  className = "",
  maxDisplayItems = 3,
  searchable = true,
  clearable = true,
  closeOnSelect = false,
  maxSelection
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm) return options;
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, searchable]);

  const displayText = useMemo(() => {
    if (!value || value.length === 0) return placeholder;
    
    const selectedOptions = options.filter(option => value.includes(option.value));
    
    if (selectedOptions.length <= maxDisplayItems) {
      return selectedOptions.map(option => option.label).join(", ");
    }
    
    return `${selectedOptions.slice(0, maxDisplayItems).map(option => option.label).join(", ")} +${selectedOptions.length - maxDisplayItems} more`;
  }, [value, options, maxDisplayItems, placeholder]);

  const handlers = useMemo(() => ({
    toggleDropdown: () => !disabled && setIsOpen(!isOpen),
    clearAll: (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange([]);
    },
    searchChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    searchClick: (e: React.MouseEvent) => e.stopPropagation(),
  }), [disabled, isOpen, onChange]);

  const toggleOption = useCallback((optionValue: string | number) => {
    const isSelected = value.includes(optionValue);
    let newValues: (string | number)[];

    if (isSelected) {
      newValues = value.filter(v => v !== optionValue);
    } else {
      if (maxSelection && maxSelection === 1) {
        newValues = [optionValue];
      } else if (maxSelection && value.length >= maxSelection) {
        return; // Don't add more
      } else {
        newValues = [...value, optionValue];
      }
    }

    onChange(newValues);

    if (closeOnSelect) {
      setIsOpen(false);
      setSearchTerm("");
    }
  }, [value, onChange, maxSelection, closeOnSelect]);

  // Click outside handler
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const containerClassName = useMemo(() => {
    const baseClass = "relative w-full min-h-[40px] border border-gray-300 rounded-md bg-white text-sm text-gray-900 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500";
    const disabledClass = disabled ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer";
    return `${baseClass} ${disabledClass} ${className}`;
  }, [disabled, className]);

  const memoizedOptionsList = useMemo(() => 
    filteredOptions.map((option, idx) => {
      const isSelected = value.includes(option.value);
      const isDisabled = option.disabled;

      return (
        <div
          key={`${name}-option-${String(option.value)}-${idx}`}
          onClick={() => !isDisabled && toggleOption(option.value)}
          className={`flex items-center px-3 py-2 text-sm cursor-pointer ${
            isDisabled ? "text-gray-400 cursor-not-allowed" : "text-gray-900 hover:bg-gray-100"
          } ${isSelected ? "bg-blue-50 text-blue-900" : ""}`}
          title={option.description}
        >
          <div className={`w-4 h-4 mr-3 border-2 rounded flex items-center justify-center ${
            isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
          } ${isDisabled ? "border-gray-200 bg-gray-100" : ""}`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <div className="font-medium">{option.label}</div>
            {option.description && (
              <div className="text-xs text-gray-500 mt-1">{option.description}</div>
            )}
          </div>
        </div>
      );
    })
  , [filteredOptions, value, name, toggleOption]);

  return (
    <div ref={dropdownRef} className={containerClassName}>
      <div className="flex items-center justify-between px-3 py-2 min-h-[36px]" onClick={handlers.toggleDropdown}>
        <span className={`flex-1 ${!value || value.length === 0 ? "text-gray-500" : "text-gray-900"}`}>
          {displayText}
        </span>
        <div className="flex items-center gap-1">
          {clearable && value && value.length > 0 && !disabled && (
            <button
              type="button"
              onClick={handlers.clearAll}
              className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
              title="Clear all"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {searchable && (
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search options..."
                value={searchTerm}
                onChange={handlers.searchChange}
                onClick={handlers.searchClick}
                className="form-input"
              />
            </div>
          )}
          <div className="py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? "No options found" : "No options available"}
              </div>
            ) : (
              memoizedOptionsList
            )}
          </div>
        </div>
      )}
    </div>
  );
});

// ===== FIELD RENDERER COMPONENT =====
interface FieldRendererProps {
  field: FormField;
  form: UseFormReturn<any>;
  loading?: boolean;
  onFieldChange?: (fieldName: string, value: any) => void;
  onVerificationStatusChange?: (fieldName: string, status: "unverified" | "verifying" | "verified" | "failed") => void;
}

const FieldRenderer = memo<FieldRendererProps>(({
  field,
  form,
  loading = false,
  onFieldChange,
  onVerificationStatusChange
}) => {
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<"unverified" | "verifying" | "verified" | "failed">(
    field.verificationStatus || "unverified"
  );
  
  const fieldValue = form.watch(field.name);
  const fieldError = form.formState.errors[field.name]?.message as string;
  
  const isDisabled = loading || field.disabled;
  const isFocused = focusedField === field.name;

  const handleChange = useCallback((value: any) => {
    form.setValue(field.name, value, { shouldValidate: true });
    onFieldChange?.(field.name, value);
    
    // Reset verification status when value changes
    if (verificationStatus === "verified" && value !== fieldValue) {
      setVerificationStatus("unverified");
      onVerificationStatusChange?.(field.name, "unverified");
    }
  }, [form, field.name, onFieldChange, verificationStatus, fieldValue, onVerificationStatusChange]);

  const handleFocus = useCallback(() => {
    setFocusedField(field.name);
  }, [field.name]);

  const handleBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  const handleVerificationStatusChange = useCallback((status: "unverified" | "verifying" | "verified" | "failed") => {

    setVerificationStatus(status);
    onVerificationStatusChange?.(field.name, status);
  }, [field.name, onVerificationStatusChange]);

  // Custom render function support
  if (field.customRender) {
    return field.customRender(field, form, fieldError);
  }

  if (field.type === "hidden") {
    return <input type="hidden" name={field.name} value={fieldValue || ""} />;
  }

  if (field.type === "form-array") {
  return (
    <div className={`w-full ${field.className || ""}`}>
      <FormArrayComponent
        name={field.name}
        form={form}
        itemFields={field.itemFields || []}
        label={field.label}
        helperText={field.helperText}
        minItems={field.minItems}
        maxItems={field.maxItems}
        className={field.className}
        showAddButton={field.showAddButton}
        showRemoveButton={field.showRemoveButton}
        showReorderButtons={field.showReorderButtons}
        addButtonText={field.addButtonText}
        emptyMessage={field.emptyMessage}
        onFieldChange={onFieldChange}
      />
    </div>
  );
}

  const renderInputWithPrefixSuffix = () => {
    const hasPrefix = field.prefix;
    const hasSuffix = field.suffix;
    const hasVerification = field.showVerifyButton;

    if (!hasPrefix && !hasSuffix && !hasVerification) {
      return (
        <FieldInput
          field={field}
          value={fieldValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          error={fieldError}
          disabled={isDisabled}
        />
      );
    }

    return (
      <div className="flex items-center">
        {hasPrefix && (
          <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-md">
            {field.prefix}
          </span>
        )}
        
        <div className={`flex-1 ${hasPrefix ? "rounded-l-none" : ""} ${(hasSuffix || hasVerification) ? "rounded-r-none" : ""}`}>
          <FieldInput
            field={field}
            value={fieldValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={fieldError}
            disabled={isDisabled}
            className={`${hasPrefix ? "rounded-l-none border-l-0" : ""} ${(hasSuffix || hasVerification) ? "rounded-r-none border-r-0" : ""}`}
          />
        </div>

        {hasSuffix && (
          <span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-r-md">
            {field.suffix}
          </span>
        )}

        {hasVerification && (
          <FieldVerification
            field={field}
            value={fieldValue}
            status={verificationStatus}
            onStatusChange={handleVerificationStatusChange}
            disabled={isDisabled}
          />
        )}
      </div>
    );
  };

  return (
    <div className={field.classes?.container || `w-full ${field.className || ""}`} style={field.style}>
      {/* Icon support */}
      {field.icon && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-gray-500">{field.icon}</span>
        </div>
      )}

      {/* Label rendering - skip for checkbox, switch, form-array as they handle their own labels */}
      {field.label && !["checkbox", "switch", "form-array"].includes(field.type) && (
        <div className="flex items-center gap-2 mb-2">
          <label 
            htmlFor={field.name} 
            className={`form-label ${field.showHelperTooltip ? '' : 'my-2'}`}
            style={field.labelStyle as React.CSSProperties}
          >
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.showHelperTooltip && field.tooltipText && (
            <Tooltip content={field.tooltipText} position={field.tooltipPosition || "top"}>
              <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
            </Tooltip>
          )}
        </div>
      )}

      {/* Input rendering with prefix/suffix/verification support */}
      {renderInputWithPrefixSuffix()}

      {/* Error and helper text */}
      <div className="space-y-1 mt-1">
        {fieldError && (
          <span className={field.classes?.error || "text-red-500 text-sm"} role="alert">
            {fieldError}
          </span>
        )}
        {!fieldError && field.helperText && !["switch", "form-array"].includes(field.type) && (
          <span className="text-gray-500 text-xs">{field.helperText}</span>
        )}
      </div>
    </div>
  );
});


// ===== FIELD INPUT COMPONENT =====
interface FieldInputProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const FieldInput = memo<FieldInputProps>(({
  field,
  value,
  onChange,
  onFocus,
  onBlur,
  error,
  disabled = false,
  className = ""
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const inputClassName = useMemo(() => {
    // If field has custom input class, use that instead of default
    if (field.classes?.input) {
      return field.classes.input;
    }
    
    const baseClass = "w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
    const errorClass = error ? "border-red-500" : "border-gray-300";
    const disabledClass = disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white";
    return `${baseClass} ${errorClass} ${disabledClass} ${className}`;
  }, [field.classes?.input, error, disabled, className]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let newValue: any = e.target.value;
    
    if (field.type === "number" || field.type === "decimal" || field.type === "negative-decimal") {
      newValue = e.target.value === "" ? "" : Number(e.target.value);
    } else if (field.type === "checkbox") {
      newValue = (e.target as HTMLInputElement).checked;
    }
    
    onChange(newValue);
  }, [field.type, onChange]);

  const handleSelectChange = useCallback((newValue: string | number) => {
    onChange(newValue);
  }, [onChange]);

  const handleMultiSelectChange = useCallback((newValues: (string | number)[]) => {
    onChange(newValues);
  }, [onChange]);

  const togglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const commonProps = useMemo(() => ({
    value: value || "",
    onChange: handleChange,
    onFocus,
    onBlur,
    disabled,
    placeholder: field.placeholder,
    className: 'form-input',
  }), [value, handleChange, onFocus, onBlur, disabled, field.placeholder, inputClassName]);

  switch (field.type) {
    case "text":
    case "email":
    case "phone":
    case "url":
      return <input {...commonProps} type={field.type} className="form-input" />;

    case "password":
      return (
        <div className="relative">
          <input
            {...commonProps}
            type={showPassword ? "text" : "password"}
            className={`form-input pr-10`}
          />
          <button
            type="button"
            onClick={togglePassword}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      );

    case "textarea":
      return (
        <textarea
          {...commonProps}
          rows={field.rows || 4}
          cols={field.cols}
          className="form-input"
        />
      );

    case "number":
    case "decimal":
    case "negative-decimal":
      return (
        <input
          {...commonProps}
          type="number"
          min={field.min}
          max={field.max}
          step={field.step || (field.type === "decimal" ? 0.01 : 1)}
        />
      );

    case "datepicker":
      return <input {...commonProps} type="date" className="form-input p-1" />;

    case "time":
      return <input {...commonProps} type="time" />;

    case "select":
      return (
        <OptimizedSelect
          name={field.name}
          options={field.options || []}
          value={value}
          onChange={handleSelectChange}
          placeholder={field.placeholder}
          disabled={disabled}
          className={field.classes?.select || className}
          onFocus={onFocus}
          onBlur={onBlur}
          error={!!error}
        /> 
      );

    case "multi-select":
      return (
        <OptimizedMultiSelect
          name={field.name}
          options={field.options || []}
          value={value || []}
          onChange={handleMultiSelectChange}
          placeholder={field.placeholder}
          disabled={disabled}
          className={className}
          maxDisplayItems={field.maxDisplayItems}
          searchable={field.searchable}
          clearable={field.clearable}
          closeOnSelect={field.closeOnSelect}
          maxSelection={field.maxSelection}
        />
      );

    case "checkbox":
      return (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={value || false}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
          />
          <label className="ml-2 text-sm text-gray-700">{field.label}</label>
        </div>
      );

    case "radio":
      return (
        <div className="flex gap-3">
          {field.options?.map((option) => (
            <label key={String(option.value)} className="flex items-center">
              <input
                type="radio"
                value={String(option.value)}
                checked={value === option.value}
                onChange={handleChange}
                disabled={disabled || option.disabled}
                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      );
      case "switch":
        return (
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className={field.classes?.label || "text-sm font-medium text-gray-900"}>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </div>
              {field.helperText && (
                <div className="text-xs text-gray-500 mt-1">{field.helperText}</div>
              )}
            </div>
            <Switch
              id={field.name}
              checked={value || false}
              disabled={disabled}
              onCheckedChange={(checked) => {
                onChange(checked);
              }}
            />
          </div>
        );

    default:
      return <input {...commonProps} type="text" />;
  }
});


// ===== EXPORTS =====
export { OptimizedSelect, OptimizedMultiSelect, FieldInput, FieldRenderer };
// ===== DYNAMIC FORM COMPONENT =====
export const DynamicFormV2: React.FC<DynamicFormProps> = memo(({
  fields,
  form,
  onSubmit,
  onReset,
  loading = false,
  config = {},
  onFieldChange,
  className = "",
  style = {},
  renderCustomFooter,
  gridColumns = 1,
  responsiveColumns,
  onVerificationStatusChange,
}) => {
  const formValues = form.watch();

  const visibleFields = useMemo(() => {
    return fields.filter(field => {
      if (!field.showWhen) return true;
      return field.showWhen(formValues, field.name);
    });
  }, [fields, formValues]);

  const groupedFields = useMemo(() => {
    const groups: Record<string, FormField[]> = {};
    
    visibleFields.forEach(field => {
      const groupName = field.group || "default";
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(field);
    });
    
    return groups;
  }, [visibleFields]);

  const gridClasses = useMemo(() => {
    let classes = [];
    if (gridColumns > 1) {
      classes.push(`grid grid-cols-${gridColumns}`);
    }
    if (responsiveColumns) {
      if (responsiveColumns.sm) classes.push(`sm:grid-cols-${responsiveColumns.sm}`);
      if (responsiveColumns.md) classes.push(`md:grid-cols-${responsiveColumns.md}`);
      if (responsiveColumns.lg) classes.push(`lg:grid-cols-${responsiveColumns.lg}`);
      if (responsiveColumns.xl) classes.push(`xl:grid-cols-${responsiveColumns.xl}`);
    }
    if (gridColumns > 1 || responsiveColumns) {
      classes.push("gap-4");
    }
    return classes.join(" ");
  }, [gridColumns, responsiveColumns]);

  const handleSubmit = useCallback(async (data: any) => {
    try {
      await onSubmit(data);
      if (config.resetOnSubmit) {
        form.reset();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  }, [onSubmit, config.resetOnSubmit, form]);

  const handleReset = useCallback(() => {
    form.reset();
    onReset?.();
  }, [form, onReset]);

  const renderFieldGroup = useCallback((groupName: string, groupFields: FormField[]) => {
    if (groupName === "default") {
      return (
        <div key={groupName} className={gridClasses}>
          {groupFields.map(field => (
            <FieldRenderer
              key={field.name}
              field={field}
              form={form}
              loading={loading}
              onFieldChange={onFieldChange}
            />
          ))}
        </div>
      );
    }

    return (
      <div key={groupName} className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">{groupName}</h3>
        <div className={gridClasses}>
          {groupFields.map(field => (
            <FieldRenderer
              key={field.name}
              field={field}
              form={form}
              loading={loading}
              onFieldChange={onFieldChange}
            />
          ))}
        </div>
      </div>
    );
  }, [gridClasses, form, loading, onFieldChange]);

  const renderActions = useCallback(() => (
    <div className="flex gap-3 pt-6 border-t border-gray-200">
      {config.showResetButton && (
        <button
          type="button"
          onClick={handleReset}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {config.resetButtonText || "Reset"}
        </button>
      )}
      {config.showSubmitButton !== false && (
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? "Submitting..." : config.submitButtonText || "Submit"}
        </button>
      )}
    </div>
  ), [config, handleReset, loading]);

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className={`space-y-6 ${className}`}
      style={style}
      noValidate
    >
      <div className="space-y-6">
        {Object.entries(groupedFields).map(([groupName, groupFields]) =>
          renderFieldGroup(groupName, groupFields)
        )}
      </div>
      {renderCustomFooter ? renderCustomFooter() : renderActions()}
    </form>
  );
});

// Display names for debugging
Tooltip.displayName = "Tooltip";
OptimizedSelect.displayName = "OptimizedSelect";
OptimizedMultiSelect.displayName = "OptimizedMultiSelect";
FieldInput.displayName = "FieldInput";
FieldRenderer.displayName = "FieldRenderer";
DynamicFormV2.displayName = "DynamicFormV2";
FormArrayComponent.displayName = "FormArrayComponent";